/**
 * monetization/quota — PURE quota evaluation (usage vs limits) for the UI.
 *
 * HARD enforcement lives on the server (Rust write path + upload); this is the
 * preflight/presentation layer: usage bars, a "near the limit" warning, and
 * disabling creates when the tier is full, so the user never
 * runs blindly into the server's 402/rejection.
 */

import {
  QUOTA_KEYS,
  type QuotaEvaluation,
  type QuotaKey,
  type QuotaLimits,
  type UsageSnapshot,
} from "./types";

export const NEAR_LIMIT_RATIO = 0.8;

export function emptyUsage(): UsageSnapshot {
  return { storage_bytes: 0, items: 0, collections: 0, orgs: 0, attachments: 0 };
}

/** Evaluates ONE quota. */
export function evaluateQuota(
  key: QuotaKey,
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation {
  const used = usage[key] ?? 0;
  const limit = limits[key] ?? null;
  if (limit === null) {
    return { key, used, limit: null, ratio: 0, nearLimit: false, exceeded: false };
  }
  const ratio = limit <= 0 ? 1 : Math.min(used / limit, 1);
  return {
    key,
    used,
    limit,
    ratio,
    nearLimit: ratio >= NEAR_LIMIT_RATIO,
    exceeded: used >= limit,
  };
}

/** Evaluates ALL quotas (stable QUOTA_KEYS order). */
export function evaluateQuotas(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation[] {
  return QUOTA_KEYS.map((key) => evaluateQuota(key, limits, usage));
}

/** Does an additional `delta` consumption fit in `key`? (create preflight). */
export function canConsume(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
  key: QuotaKey,
  delta = 1,
): boolean {
  const limit = limits[key] ?? null;
  if (limit === null) {
    return true;
  }
  return (usage[key] ?? 0) + delta <= limit;
}

/** Claves actualmente excedidas. */
export function exceededKeys(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaKey[] {
  return QUOTA_KEYS.filter((key) => evaluateQuota(key, limits, usage).exceeded);
}

/** Compact byte format for usage bars (10.5 MB, 2 GB…). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1);
  return `${rounded} ${units[unit]}`;
}

/** Displayable value for a limit (`null` ⇒ "∞"). */
export function formatLimit(key: QuotaKey, limit: number | null): string {
  if (limit === null) {
    return "∞";
  }
  return key === "storage_bytes" ? formatBytes(limit) : String(limit);
}

/** Displayable usage value. */
export function formatUsed(key: QuotaKey, used: number): string {
  return key === "storage_bytes" ? formatBytes(used) : String(used);
}
